import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AvatarImage } from './AvatarImage';
import { cn } from '@/lib/utils';
import { MapPin, Clock, Calendar, Building2, Mail } from 'lucide-react';

export function UserTooltip({ user, children, className, side = "bottom" }) {
  if (!user) return children;

  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const calculatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    // Tooltip dimensions (approximate, hardcoded to match w-80 = 320px)
    const TOOLTIP_WIDTH = 320;
    const GAP = 12; // mt-3 is roughly 12px

    let top = 0;
    let left = 0;

    switch (side) {
      case 'right':
        top = rect.top + scrollY + rect.height / 2; // Center vertically
        left = rect.right + scrollX + GAP;
        break;
      case 'left':
        top = rect.top + scrollY + rect.height / 2;
        left = rect.left + scrollX - TOOLTIP_WIDTH - GAP;
        break;
      case 'top':
        top = rect.top + scrollY - GAP; // Bottom of tooltip will need transform
        left = rect.left + scrollX + rect.width / 2;
        break;
      case 'bottom':
      default:
        top = rect.bottom + scrollY + GAP;
        left = rect.left + scrollX + rect.width / 2;
        break;
    }

    setCoords({ top, left });
  };

  const handleMouseEnter = () => {
    calculatePosition();
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  // Update position on scroll/resize if visible
  useEffect(() => {
    if (!isVisible) return;
    window.addEventListener('scroll', calculatePosition);
    window.addEventListener('resize', calculatePosition);
    return () => {
      window.removeEventListener('scroll', calculatePosition);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isVisible]);

  // Calculate local time
  const getLocalTime = () => {
    try {
      return new Date().toLocaleTimeString('en-US', {
        timeZone: user.time_zone || 'America/Mexico_City',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const hiredDate = user.hired_on ? new Date(user.hired_on).toLocaleDateString('es-MX', { month: 'short', year: 'numeric' }) : null;

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn("relative inline-block", className)}
      >
        {children}
      </div>

      {isVisible && createPortal(
        <div
          className="fixed z-[9999] w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden font-montserrat text-left pointer-events-none animate-in fade-in zoom-in-95 duration-200"
          style={{
            top: coords.top,
            left: coords.left,
            // Adjust transforms based on side to aligning accurately
            transform: side === 'right' ? 'translateY(-50%)' :
              side === 'left' ? 'translateY(-50%)' :
                side === 'top' ? 'translate(-50%, -100%)' :
                  'translate(-50%, 0)'
          }}
        >
          {/* Header Banner */}
          <div className="h-16 bg-gradient-to-r from-bambu-green to-bambu-dark-green relative">
            <div className="absolute -bottom-8 left-6">
              <AvatarImage
                user={user}
                src={user.avatar_url}
                size={64}
                className="border-4 border-white shadow-md bg-white"
              />
              <div className="absolute bottom-1 right-1 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></div>
            </div>
          </div>

          {/* Content */}
          <div className="pt-10 px-6 pb-6">
            <div className="mb-4">
              <h4 className="font-bold text-lg text-gray-900 leading-tight">{user.full_name}</h4>
              <p className="text-sm text-gray-500 font-medium">{user.job_title || 'Colaborador'}</p>
            </div>

            <div className="space-y-2 mb-4">
              {/* Location */}
              {(user.location || user.country) && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{user.location}{user.location && user.country ? ', ' : ''}{user.country}</span>
                </div>
              )}

              {/* Department */}
              {user.department && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span>{user.department?.name}</span>
                </div>
              )}
            </div>

            {/* Stats Divider */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-bold text-gray-400 uppercase mb-3">Interacciones</p>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Reconocimientos de ti</span>
                <span className="font-bold text-gray-900">0</span>
              </div>
              <div className="flex justify-between items-center text-sm mt-2">
                <span className="text-gray-600">Reconocimientos hacia ti</span>
                <span className="font-bold text-gray-900">0</span>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
